import { AlertTriangle, CalendarClock, Clock, FileClock, ShoppingCart, Wallet, type LucideIcon } from 'lucide-react';
import { KpiCard } from '@/shared/ui';
import { money, num } from '@/shared/formatters';
import type { ProcurementScope, ProcurementSummary } from '@contracts/procurement';
import { useProcurementFilters } from '@/features/procurement/procurementContext';

/** Solo las métricas numéricas del resumen (excluye `currency`). */
type NumericSummaryKey = Exclude<keyof ProcurementSummary, 'currency'>;

interface KpiDef {
  key: NumericSummaryKey;
  label: string;
  gradient: string;
  Icon: LucideIcon;
  money?: boolean;
  /** Explica de dónde sale el número (los de dinero lo necesitan). */
  hint?: string;
  /** Filtro que aplica al hacer click (drill-down sobre la lista). */
  filter?: Partial<ProcurementScope>;
}

const KPIS: KpiDef[] = [
  { key: 'pendingAuth', label: 'Pendientes de autorizar', gradient: 'from-sky-400 via-blue-500 to-blue-700', Icon: FileClock, filter: { states: ['nueva'] } },
  { key: 'overdue', label: 'Vencidas', gradient: 'from-red-500 via-red-600 to-rose-700', Icon: AlertTriangle, filter: { slaState: 'overdue' } },
  { key: 'dueSoon', label: 'Próximas a vencer', gradient: 'from-amber-400 via-orange-500 to-orange-600', Icon: Clock, filter: { slaState: 'due_soon' } },
  { key: 'urgentOpen', label: 'Urgentes abiertas', gradient: 'from-fuchsia-400 via-fuchsia-500 to-pink-600', Icon: ShoppingCart, filter: { urgency: 'urgent' } },
  // El DINERO va dividido (pedido del cliente): lo pendiente de autorizar es
  // solo PREVISTO; comprometido de verdad es lo autorizado + en compra.
  { key: 'forecastAmount', label: 'Monto previsto', gradient: 'from-violet-400 via-violet-500 to-purple-700', Icon: CalendarClock, money: true, hint: 'Por autorizar — aún NO comprometido', filter: { states: ['nueva'] } },
  { key: 'committedAmount', label: 'Monto comprometido', gradient: 'from-emerald-400 via-emerald-500 to-teal-700', Icon: Wallet, money: true, hint: 'Autorizadas + en compra' },
];

export function ProcurementKpis({ summary, loading }: { summary?: ProcurementSummary; loading?: boolean }) {
  const { patchScope } = useProcurementFilters();

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
      {KPIS.map((kpi) => (
        <KpiCard
          key={kpi.key}
          label={kpi.label}
          value={loading || !summary ? 0 : summary[kpi.key]}
          format={kpi.money ? money : num}
          gradient={kpi.gradient}
          Icon={kpi.Icon}
          onClick={kpi.filter ? () => patchScope(kpi.filter as Partial<ProcurementScope>) : undefined}
          hint={kpi.hint ?? (kpi.filter ? 'Filtrar' : undefined)}
        />
      ))}
    </div>
  );
}
